
    // IMPORTS
    import { computed, defineComponent, PropType } from 'vue'
    import { buildBomItemLocalization, buildLocalization } from '@/util'

    import { Button, ButtonGroup } from '@progress/kendo-vue-buttons'

    import { MaterialListItem, MaterialTreeItem } from '@/types/material'
    import { BomTreeNodeType } from '@/types/bom'

    export default defineComponent({
        name: 'material-explode-bar',

        components: {
            'k-button': Button,
            ButtonGroup
        },

        props: {
            item: { type: Object as PropType<MaterialTreeItem>, required: true }
        },

        emits: {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            toggleChildNode: (item: MaterialTreeItem, nodeType: BomTreeNodeType) => true,

            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            showDetails: (item: MaterialListItem) => true
        },

        setup(props, { emit }) {
            /********************************************************************************************************************
             COMPUTED PROPERTIES
             ********************************************************************************************************************/
            const iconPathProduction = computed(() => getIconPath('production'))
            const iconPathAlternativeBom = computed(() => getIconPath('multiple'))
            const iconPathBoughtTogether = computed(() => getIconPath('recommended-items'))
            const iconPathMaintenance = computed(() => getIconPath('maintenance'))
            const iconPathAlternativeItems = computed(() => getIconPath('alternatives'))
            const iconPathReplaced = computed(() => getIconPath('replaced'))

            const showProductionBomButton = computed(() => props.item.hasProductionBom === true || props.item.hasRecursiveProductionBom === true)
            const showAlternativeBomNodeButton = computed(() => props.item.hasSeveralAlternativeBoms === true)
            const showReplacementButton = computed(() => props.item.hasReplacement === true)
            const showRecommendedItemsButton = computed(() => props.item.hasBoughtWithBom === true || props.item.hasRecursiveBoughtWithBom === true)
            const showMaintenanceBomButton = computed(() => props.item.hasMaintenanceBom === true || props.item.hasRecursiveMaintenanceBom === true)
            const showAlternativeItemsButton = computed(() => props.item.hasAlternativeItemsBom === true || props.item.hasRecursiveAlternativeItemsBom === true)

            const productionBomButtonDisabled = computed(() => props.item.hasRecursiveProductionBom === true)
            const recommendedItemsButtonDisabled = computed(() => props.item.hasRecursiveBoughtWithBom === true)
            const maintenanceBomButtonDisabled = computed(() => props.item.hasRecursiveMaintenanceBom === true)
            const alternativeItemsButtonDisabled = computed(() => props.item.hasRecursiveAlternativeItemsBom === true)

            const computedData = {
                iconPathProduction,
                iconPathMaintenance,
                iconPathBoughtTogether,
                iconPathAlternativeBom,
                iconPathAlternativeItems,
                iconPathReplaced,
                showProductionBomButton,
                showAlternativeBomNodeButton,
                showReplacementButton,
                showRecommendedItemsButton,
                showMaintenanceBomButton,
                showAlternativeItemsButton,
                productionBomButtonDisabled,
                recommendedItemsButtonDisabled,
                maintenanceBomButtonDisabled,
                alternativeItemsButtonDisabled
            }

            /********************************************************************************************************************
             PUBLIC METHODS
             ********************************************************************************************************************/
            const { localize } = buildLocalization();
            const { localizeBomItemText } = buildBomItemLocalization()

            function showDetails(item: MaterialListItem) {
                emit('showDetails', item)
            }

            function getPictureIndicatorClasses(item: MaterialListItem): string[] {
                const classes = []

                if (item.hasPicture) {
                    classes.push('k-icon')
                    classes.push('k-i-photo-camera')
                }

                return classes
            }

            function getPdfFileIndicatorClasses(item: MaterialListItem): string[] {
                const classes = []

                if (item.documents?.find(doc => doc.documentType != 'RES')) {
                    classes.push('k-icon')
                    classes.push('k-i-file-pdf')
                }

                return classes
            }

            function toggleProductionNode(item: MaterialTreeItem) {
                toggleChildNode(item, BomTreeNodeType.ProductionNode)
            }

            function toggleBoughtWithNode(item: MaterialTreeItem) {
                toggleChildNode(item, BomTreeNodeType.RecommendedItemsNode)
            }

            function toggleMaintenanceNode(item: MaterialTreeItem) {
                toggleChildNode(item, BomTreeNodeType.MaintenanceNode)
            }

            function toggleAlternativeItemsNode(item: MaterialTreeItem) {
                toggleChildNode(item, BomTreeNodeType.AlternativeItemsNode)
            }

            function toggleAlternativeBomNode(item: MaterialTreeItem) {
                toggleChildNode(item, BomTreeNodeType.AlternativeBomNode)
            }

            function toggleReplacementNode(item: MaterialTreeItem) {
                toggleChildNode(item, BomTreeNodeType.ReplacementNode)
            }

            const publicMethods = {
                localize,
                localizeBomItemText,
                showDetails,
                getPictureIndicatorClasses,
                getPdfFileIndicatorClasses,
                toggleProductionNode,
                toggleBoughtWithNode,
                toggleMaintenanceNode,
                toggleAlternativeItemsNode,
                toggleAlternativeBomNode,
                toggleReplacementNode
            }

            /********************************************************************************************************************
             PRIVATE METHODS
             ********************************************************************************************************************/
            function toggleChildNode(item: MaterialTreeItem, nodeType: BomTreeNodeType) {
                emit('toggleChildNode', item, nodeType)
            }

            function getIconPath(iconName: string): string {
                return require(`@/img/icon-${iconName}.svg`)
            }

            /********************************************************************************************************************
             COMPONENT INTERFACE
             ********************************************************************************************************************/
            return { ...computedData, ...publicMethods }
        }
    });
